// 入院実績(前年度・予算比較) β版
// 入院KPI と同じ主要8指標を、指定月範囲の月平均で 今年度 / 予算 / 前年度 比較する。
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import * as Plot from "@observablehq/plot";
import { api } from "../lib/api";
import { useFilters } from "../store";
import { PlotFigure } from "./Plot";
import { ContextBar } from "./ContextBar";
import { buildMonthAxisMark } from "../lib/chartAxis";
// [2026-09-07] 年度枠・期間ヘルパは 行為別売上 と共有するため lib/fiscalYear へ切り出し
import { FY_MONTHS, fiscalYearOf, fyDate, monthLabel, selectedMonthDates, selectedRangeLabel } from "../lib/fiscalYear";
import { fmtCardMain, fmtYenScaled, fmtYenAxisTick, isYenCardMetric, yenUseOku, fmtByUnitValueOnly, fmtRatio, fmtRatioPct } from "../lib/format";
import { colorForRatio, colorForYoy } from "./KpiBigPanel";
import { resolveAllSpecs, resolveMetric, BUDGET_TAB_SPECS, COMPARE_EXTRA_SPECS, type ResolvedMetric, type MetricSpec } from "./prototypes/budgetSpec";
import { RATIO_METRICS, weightedRatio } from "../lib/ratioMetrics";
import { InpatientDoctorMatrix } from "./InpatientDoctorMatrix";
import type { DeptMatrixResponse, DeptMetricsAllResponse, Direction, MetricRow } from "@shared/api";

type ColorMode = "yoy" | "budget";

const SKIP_ZERO_KEYS = new Set(["GP", "GP単価", "GP単価_在院", "出来高換算薬剤費", "出来高換算材料費", "出来高換算薬剤材料費"]);

// 達成=薄青 / 未達=薄赤 の 2 色のみ (2026-07-10 ユーザ指示: 文字色の 青=Good/赤=Bad と揃える・濃淡段階は廃止)
// [2026-08-03] 医師別マトリックス (InpatientDoctorMatrix) も同じ配色にするため export。
export function cellBg(pct: number | null, direction: Direction): string {
  if (pct == null || direction === "neutral") return "";
  const p = direction === "lower_is_better" ? -pct : pct;
  return p >= 0 ? "bg-blue-100" : "bg-red-100";
}

function gradeOf(cur: number | null, budget: number | null, prevYear: number | null, direction: Direction): "A" | "B" | "C" | null {
  if (cur == null || direction === "neutral") return null;
  const beats = (ref: number | null): boolean | null =>
    ref == null ? null : (direction === "lower_is_better" ? cur <= ref : cur >= ref);
  const budOk = beats(budget), pyOk = beats(prevYear);
  if (budOk === true) return "A";
  if (pyOk === true) return "B";
  if (budOk == null && pyOk == null) return null;
  return "C";
}

function gradeStyle(g: "A" | "B" | "C"): string {
  return g === "A" ? "bg-blue-600 text-white" : g === "B" ? "bg-amber-400 text-white" : "bg-red-500 text-white";
}

// カード / 月別マトリックスの 億円⇄万円 は「期間内の最大額」で自動判定する。
// [2026-08-03 ユーザ判断] 一時期ここに「入院収入は科の規模に関係なく常に億円」を入れていたが、
//   月額 1 億円未満の 13 科 (緩和ケア内科 304万→0.03億 / 眼科 582万→0.06億 等) で桁が潰れて
//   読めなくなるため撤去した。★億円固定でよいのは**科別マトリックスだけ**で、そちらは
//   DeptMatrix 側が「病院全体を含む列内 max」で判定するため実質常に億円になる (別ロジック)。
function useOkuFor(r: ResolvedMetric): boolean {
  return isYenCardMetric(r.spec.key, r.spec.unit) && yenUseOku(Math.max(0, ...r.trend.map((p) => Math.abs(p.actual ?? 0))));
}

function displayValue(r: ResolvedMetric, v: number | null | undefined, withUnit = false): string {
  if (v == null) return "—";
  const isYen = isYenCardMetric(r.spec.key, r.spec.unit);
  if (isYen) {
    const f = fmtYenScaled(v, useOkuFor(r));
    return withUnit ? `${f.value} ${f.unit}` : f.value;
  }
  const s = fmtByUnitValueOnly(v, r.spec.unit);
  return withUnit && r.spec.unit ? `${s} ${r.spec.unit}` : s;
}

export function meanSelected(r: ResolvedMetric, months: Set<string>, field: "actual" | "budget" | "prevYear"): number | null {
  const skipZero = SKIP_ZERO_KEYS.has(r.spec.key);
  const vals = r.trend
    .filter((p) => months.has(p.date))
    .map((p) => p[field])
    .filter((v): v is number => v != null && Number.isFinite(v) && !(skipZero && v === 0));
  return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
}

// ── 比指標(単価/率/平均在院日数)の期間値 = Σ分子/Σ分母 (加重・2026-07-13) ──
// 月別比の単純平均 (平均の平均) は volume 加重が消えて誤る (正本 = lib/ratioMetrics.ts)。
// 分子分母の系列が揃わない場合 (病床率系の予算 = 注入定数のみ 等) は従来の単純平均へフォールバック。
type TrendPointLike = { date: string; actual: number | null; budget: number | null; prevYear: number | null };
export type TrendOf = (metric: string) => TrendPointLike[] | undefined;
type PeriodCalc = (r: ResolvedMetric, field: "actual" | "budget" | "prevYear") => number | null;
function admissionVolumeLabel(key: string, yotei: string): string | null {
  if (yotei !== "予定" && yotei !== "緊急") return null;
  if (key === "新規入院数") return `${yotei}入院数`;
  if (key === "新規受持数") return `新規受持数（${yotei}）`;
  return null;
}
export function periodValue(
  key: string, field: "actual" | "budget" | "prevYear",
  months: Set<string>, trendOf: TrendOf,
  fallback: () => number | null,
  yotei: string = "合計",
): number | null {
  const spec = (yotei !== "合計" && (key === "予定入院率" || key === "緊急入院率"))
    ? { num: ["入院構成_分子"], den: ["入院構成_分母"] }
    : RATIO_METRICS[key];
  if (spec && !spec.unitDays) {
    const sumOf = (m: string): number | null => {
      const tr = trendOf(m);
      if (!tr) return null;
      const vals = tr.filter((p) => months.has(p.date)).map((p) => p[field])
        .filter((v): v is number => v != null && Number.isFinite(v));
      return vals.length ? vals.reduce((a, b) => a + b, 0) : null;
    };
    const w = weightedRatio(spec, sumOf);
    if (w != null) return w;
  }
  return fallback();
}

function resolvePanelKeys(revenueBase: "入院収入" | "粗利", volumeBase: "入院" | "在院", focused: boolean, yotei: string): string[] {
  const revenue = revenueBase === "粗利" ? "GP" : "入院収入";
  const volume = volumeBase === "在院" ? "在院患者延数" : "入院患者延数";
  const unit =
    revenueBase === "入院収入" && volumeBase === "入院" ? "入院単価" :
    revenueBase === "入院収入" && volumeBase === "在院" ? "在院単価" :
    revenueBase === "粗利" && volumeBase === "入院" ? "GP単価" : "GP単価_在院";
  const admissionRate = yotei === "緊急" ? "緊急入院率" : "予定入院率";
  return [revenue, volume, unit, "平均在院日数", focused ? "新規受持数" : "新規入院数", admissionRate, "紹介入院率", "DPC期間2以内率"];
}

// 月別マトリックスの行順 (2026-07-10 ユーザ指示で固定指標 → 2026-07-13 延数基準トグル連動に変更:
// サイドバーの延数基準=在院 のとき 入院患者延数→在院患者延数 / 入院単価→在院単価 が連動して入替)。
// indent=全角1字下げ (親指標の内訳)、thin=指標名を細文字に。
// 新規入院系: 病院全体=新規入院数のみ / 科フォーカス=新規受持数 + 内訳(新規入院数・他科からの転科)。
type MatrixRowDef = { key: string; thin?: boolean; indent?: boolean };
type ResolvedMatrixRow = MatrixRowDef & { r: ResolvedMetric };
function matrixRowDefs(focused: boolean, volumeBase: "入院" | "在院", yotei: string): MatrixRowDef[] {
  const zaiin = volumeBase === "在院";
  return [
    { key: "入院収入" },
    { key: "手術収入", thin: true, indent: true },
    { key: "手術件数", thin: true, indent: true },
    { key: zaiin ? "在院患者延数" : "入院患者延数" },
    // 病院全体のときだけ 延数の直下に病床指標 (2026-07-13): 入院延数=病床稼働率(入院患者延数÷延病床数) /
    // 在院延数=病床利用率(在院患者延数÷延病床数)。科フォーカス時は科別の意味が薄いため出さない。
    ...(focused ? [] : [{ key: zaiin ? "病床利用率" : "病床稼働率", thin: true, indent: true }]),
    { key: zaiin ? "在院単価" : "入院単価" },
    { key: "平均在院日数" },
    ...(focused
      ? [{ key: "新規受持数" }, { key: "新規入院数", thin: true, indent: true }, { key: "他科から転科", thin: true, indent: true }]
      : [{ key: "新規入院数" }]),
    { key: yotei === "緊急" ? "緊急入院率" : "予定入院率" },
    { key: "紹介入院率" },
    { key: "DPC期間2以内率" },
    { key: "常勤換算数", thin: true },
    { key: "内専攻医常勤換算数", thin: true, indent: true },
  ];
}

export function InpatientBudgetComparePanel({ metrics, builtAt, dateMax, showDoctor = false }: {
  metrics: MetricRow[]; builtAt: string; dateMax: string;
  /** [2026-08-03] 入院実績(医師別) タブ (β): 科別マトリックスの下に医師別マトリックスを足す。
   *  正式版タブは showDoctor=false のままなので表示・印刷とも一切変わらない。 */
  showDoctor?: boolean;
}) {
  const f = useFilters();
  const fy = fiscalYearOf(dateMax);
  const fyStart = `${fy}-04-01`;
  const fyEnd = `${fy + 1}-03-01`;
  const selectedMonths = useMemo(() => selectedMonthDates(fy, f.periodFrom, f.periodTo), [fy, f.periodFrom, f.periodTo]);
  const targetName = f.focusedDept ? `🎯 ${f.focusedDept}` : "🏥 病院全体";
  const rangeLabel = selectedRangeLabel(f.periodFrom, f.periodTo);
  const estimatedBudget = f.yotei !== "合計" && f.iryo === "合計" && f.routeKeiyu === "合計";

  // 色分け/下段の基準 (前年度比/予算比) — 月別マトリックスと科別マトリックスで共通 (2026-07-13 リフト)
  const [colorMode, setColorMode] = useState<ColorMode>("yoy");

  const { data, isLoading, error } = useQuery<DeptMetricsAllResponse>({
    queryKey: ["inpatient_budget_compare", f.granularity, f.iryo, f.yotei, fyEnd, fyStart, f.focusedDept, f.routeKeiyu],
    queryFn: () => api.deptMetricsAll({
      granularity: f.granularity, iryo: f.iryo, yotei: f.yotei,
      asOfMonth: fyEnd, periodFrom: fyStart, focusedDept: f.focusedDept ?? undefined, trendBack: 11,
      keiyu: f.routeKeiyu,
    }),
    placeholderData: (prev) => prev,
  });

  const { cardResolved, matrixRows, trendOf } = useMemo(() => {
    if (!data) return { cardResolved: [] as ResolvedMetric[], matrixRows: [] as ResolvedMatrixRow[], trendOf: (() => undefined) as TrendOf };
    const byKey = new Map([
      ...resolveAllSpecs(data).map((r) => [r.spec.key, r] as const),
      ...COMPARE_EXTRA_SPECS.map((s) => [s.key, resolveMetric(s, data)] as const),
    ]);
    // 指標キーと集計値は維持し、入院区分を絞った画面だけ見出しを明確にする。
    for (const key of ["新規入院数", "新規受持数"]) {
      const r = byKey.get(key);
      const label = admissionVolumeLabel(key, f.yotei);
      if (r && label) byKey.set(key, { ...r, spec: { ...r.spec, label } });
    }
    // 病床率系の予算 (ユーザ確定 2026-07-13・FY2025/FY2026。年度が変わったらここを見直す):
    //   分母 = 許可病床 470 床 × 日数。在院延数の予算 = 毎日 466 床。
    //   病床利用率の予算 = (466×日数) / (470×日数) = 99.1% (定数)
    //   病床稼働率の予算 = (466×日数 + 退院患者数予算) / (470×日数) (稼働率の分子 = 在院 + 退院)
    // fact_budget_all に率予算が無いため UI 側で計算注入。
    {
      const BED_BUDGET_FY = new Set([2025, 2026]);
      const entBudgetByDate = new Map((byKey.get("退院患者数")?.trend ?? []).map((p) => [p.date, p.budget]));
      const injectBedBudget = (key: string, calc: (date: string) => number | null) => {
        const r = byKey.get(key);
        if (!r) return;
        byKey.set(key, { ...r, trend: r.trend.map((p) => {
          const b = calc(p.date);
          return b != null ? { ...p, budget: b } : p;
        }) });
      };
      injectBedBudget("病床利用率", (d) => BED_BUDGET_FY.has(fiscalYearOf(d)) ? 466 / 470 : null);
      injectBedBudget("病床稼働率", (d) => {
        if (!BED_BUDGET_FY.has(fiscalYearOf(d))) return null;
        const ent = entBudgetByDate.get(d);
        if (ent == null) return null;
        const [y, m] = d.split("-").map(Number);
        const days = new Date(y, m, 0).getDate();
        return (466 * days + ent) / (470 * days);
      });
    }
    const cardResolved = resolvePanelKeys(f.revenueBasis, f.kpiVolumeBase, !!f.focusedDept, f.yotei)
      .map((k) => byKey.get(k))
      .filter((r): r is ResolvedMetric => !!r);
    const matrixRows = matrixRowDefs(!!f.focusedDept, f.kpiVolumeBase, f.yotei)
      .map((d) => ({ ...d, r: byKey.get(d.key) }))
      .filter((d): d is ResolvedMatrixRow => !!d.r);
    // 比指標の加重計算用: byKey (病床率予算の注入済み) 優先・無ければ生レスポンス
    // (退院患者数/延病床数/紹介入院数 等 spec 外の指標は data.rows から引く)
    const rawTrend = new Map(data.rows.map((r) => [r.集計区分, r.trend]));
    const trendOf: TrendOf = (m) => byKey.get(m)?.trend ?? rawTrend.get(m);
    return { cardResolved, matrixRows, trendOf };
  }, [data, f.revenueBasis, f.kpiVolumeBase, f.focusedDept, f.yotei]);

  // xlsx「出力条件」シートの共通行 (どの条件で出力されたデータかを追跡できるように)
  const condRows: Record<string, unknown>[] = [
    { 項目: "対象年度", 値: `${fy}年度` },
    { 項目: "期間 (月平均の対象)", 値: rangeLabel },
    { 項目: "診療科粒度", 値: `${f.granularity}分類` },
    { 項目: "医療区分", 値: f.iryo },
    { 項目: "予定緊急", 値: f.yotei },
    { 項目: "予定緊急別予算", 値: estimatedBudget ? "元予算を前年同月・同科・同指標の予定緊急構成比で推計配分。前年欠損時は予算の予定入院率を使用" : "年度予算表の値（医療区分・経由で絞る場合は予算非表示）" },
    { 項目: "収入の基準", 値: f.revenueBasis },
    { 項目: "延数の基準", 値: `${f.kpiVolumeBase}延数` },
    { 項目: "比指標の期間集計", 値: "Σ分子 ÷ Σ分母 (加重)。加算指標は月別値の平均" },
    { 項目: "値の単位", 値: "金額=千円 / 単価=円/日 / %系=×100 の生値 (画面の億円等の表示スケール未適用)" },
    { 項目: "データ更新 (DB build)", 値: builtAt },
    { 項目: "出力日時", 値: new Date().toLocaleString("ja-JP") },
  ];

  return (
    <div className="space-y-3">
      <section className="card p-3 no-print">
        <div className="flex items-center gap-3 flex-wrap">
          <h2 className="text-base font-semibold">
            {showDoctor ? "🧑‍⚕️ 入院実績(医師別)" : "📈 入院実績(前年度・予算比較)"}
          </h2>
          <span className="text-xs text-slate-500">
            {targetName} / 期間 <b>{rangeLabel}</b> の月平均。
            {isLoading && <span className="ml-2 text-slate-400">loading…</span>}
          </span>
        </div>
      </section>
      {error && (
        <div className="card p-4 text-sm text-red-700 bg-red-50">
          データ取得エラー: {(error as Error).message}
        </div>
      )}
      {data && (
        <>
          <div className="budget-print-page inpatient-budget-print space-y-2">
            <ContextBar forPrint metrics={metrics} builtAt={builtAt} />
            <Header targetName={targetName} rangeLabel={rangeLabel} estimatedBudget={estimatedBudget} />
            <CompareCards resolved={cardResolved} fy={fy}
                          referralCountTrend={trendOf("紹介入院数") ?? []}
                          periodCalc={(r, field) => periodValue(r.spec.key, field, selectedMonths, trendOf, () => meanSelected(r, selectedMonths, field), f.yotei)} />
            <CompareMatrix rows={matrixRows} fy={fy} selectedMonths={selectedMonths}
                           colorMode={colorMode} setColorMode={setColorMode}
                           periodCalc={(r, field) => periodValue(r.spec.key, field, selectedMonths, trendOf, () => meanSelected(r, selectedMonths, field), f.yotei)}
                           targetName={targetName} rangeLabel={rangeLabel} condRows={condRows} />
          </div>
          {/* [2026-08-03] 印刷のページ構成 (医師別タブ):
               ・科フォーカスあり = 科別の「その 1 行」+ 医師別 を 1 つの print page にまとめて A4 横 1 枚
                 (.dept-doctor-print-combined。科別の他の行は印刷時のみ非表示にする)
               ・科フォーカスなし = 科別 (2 枚目) → 医師別 (3 枚目) と別ページに分ける
             正式版タブ (showDoctor=false) は従来どおり 科別のみ 2 枚目。 */}
          {showDoctor && f.focusedDept ? (
            <div className="budget-print-page dept-matrix-print dept-doctor-print-combined space-y-2">
              <DeptMatrix fy={fy} selectedMonths={selectedMonths} colorMode={colorMode}
                          focusedDept={f.focusedDept} revenueBase={f.revenueBasis} volumeBase={f.kpiVolumeBase}
                          rangeLabel={rangeLabel} condRows={condRows} focusRowOnly />
              <InpatientDoctorMatrix fy={fy} selectedMonths={selectedMonths} focusedDept={f.focusedDept}
                                     revenueBase={f.revenueBasis} volumeBase={f.kpiVolumeBase}
                                     rangeLabel={rangeLabel} condRows={condRows}
                                     topN={f.doctorTopN} colorMode={colorMode} />
            </div>
          ) : (
            <>
              <div className="budget-print-page dept-matrix-print">
                <DeptMatrix fy={fy} selectedMonths={selectedMonths} colorMode={colorMode}
                            focusedDept={f.focusedDept} revenueBase={f.revenueBasis} volumeBase={f.kpiVolumeBase}
                            rangeLabel={rangeLabel} condRows={condRows} />
              </div>
              {showDoctor && (
                <div className="budget-print-page doctor-matrix-print">
                  <InpatientDoctorMatrix fy={fy} selectedMonths={selectedMonths} focusedDept={null}
                                         revenueBase={f.revenueBasis} volumeBase={f.kpiVolumeBase}
                                         rangeLabel={rangeLabel} condRows={condRows}
                                         topN={f.doctorTopN} colorMode={colorMode} />
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

function Header({ targetName, rangeLabel, estimatedBudget }: { targetName: string; rangeLabel: string; estimatedBudget: boolean }) {
  return (
    <div className="flex items-center gap-3 flex-wrap">
      <h2 className="text-xl font-bold">{targetName} 入院実績({rangeLabel}の月平均)</h2>
      {estimatedBudget && <span className="text-[11px] text-amber-800">※予定/緊急別予算は前年同月構成比による推計</span>}
      <span className="text-xs text-slate-500 flex items-center gap-1.5">
        <span className="grade-circle grade-circle-sm rounded-full bg-blue-600 text-white font-bold text-[11px]">A</span>予算達成
        <span className="grade-circle grade-circle-sm rounded-full bg-amber-400 text-white font-bold text-[11px]">B</span>予算未達でも前年超
        <span className="grade-circle grade-circle-sm rounded-full bg-red-500 text-white font-bold text-[11px]">C</span>それ以外
      </span>
      <div className="ml-auto flex items-center gap-3 text-xs text-slate-500">
        <span className="flex items-center gap-1"><span style={{ width: 16, height: 3, background: "#1B9E77", display: "inline-block" }}></span>今年度実績</span>
        <span className="flex items-center gap-1"><span style={{ width: 16, height: 0, borderTop: "2.5px dashed #d97706", display: "inline-block" }}></span>今年度予算</span>
        <span className="flex items-center gap-1"><span style={{ width: 16, height: 0, borderTop: "2px solid #94a3b8", opacity: 0.7, display: "inline-block" }}></span>前年度実績</span>
      </div>
    </div>
  );
}

export function CompareCards({ resolved, fy, periodCalc, referralCountTrend }: { resolved: ResolvedMetric[]; fy: number; periodCalc: PeriodCalc; referralCountTrend?: TrendPointLike[] }) {
  const xDomain = useMemo<[Date, Date]>(() => [new Date(fyDate(fy, 4)), new Date(fyDate(fy, 3))], [fy]);
  return (
    <section>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 print-4cols">
        {resolved.map((r) => <CompareCard key={r.spec.key} r={r} xDomain={xDomain} periodCalc={periodCalc} referralCountTrend={referralCountTrend} />)}
      </div>
    </section>
  );
}

function CompareCard({ r: original, xDomain, periodCalc, referralCountTrend }: { r: ResolvedMetric; xDomain: [Date, Date]; periodCalc: PeriodCalc; referralCountTrend?: TrendPointLike[] }) {
  const referralRate = original.spec.key === "紹介入院率" && referralCountTrend !== undefined ? original : null;
  const r: ResolvedMetric = referralRate ? {
    ...original,
    spec: { ...original.spec, key: "紹介入院数", label: "紹介入院件数", unit: "件" },
    trend: referralCountTrend ?? [],
  } : original;
  // 期間値: 比指標は Σ分子/Σ分母 (加重)・加算系は月平均 (2026-07-13 平均の平均を排除)
  const cur = periodCalc(r, "actual");
  const bud = periodCalc(r, "budget");
  const py = periodCalc(r, "prevYear");
  const budRatio = cur != null && bud != null && bud !== 0 ? cur / bud : null;
  const grade = gradeOf(cur, bud, py, r.spec.direction);
  const isYen = isYenCardMetric(r.spec.key, r.spec.unit);
  const main = isYen ? fmtYenScaled(cur, useOkuFor(r)) : fmtCardMain(cur, r.spec.unit, r.spec.key);

  return (
    <div className="card relative border-l-4 border-l-brand p-3 min-w-0 inpatient-compare-card">
      {grade && (
        <span className={`grade-circle grade-circle-card absolute top-1.5 right-1.5 rounded-full text-sm font-extrabold ${gradeStyle(grade)}`}
              title="期間指定月の今年度月平均で判定">{grade}</span>
      )}
      {/* タイトル・予算比/前年比の色分け(達成=青/未達=赤)は 入院KPI カードと同デザイン (2026-07-10)。
          単位はタイトル括弧でなく主数値の後ろに (入院収入カードの「14.39 億円」書式に全指標を統一) */}
      <div className="text-base font-bold text-slate-700 leading-tight pr-7 mb-1 compare-card-title">
        {r.spec.label}
      </div>
      <div className="text-[12px] leading-tight text-left compare-card-body">
        <div className="flex items-baseline gap-x-2 flex-wrap">
          <span className="text-2xl font-extrabold tabular-nums text-slate-900 compare-card-main">
            {main.value}{main.value !== "—" && main.unit && <span className="text-sm font-bold text-slate-400 ml-1">{main.unit}</span>}
          </span>
          {referralRate
            ? <span className="text-slate-500">前年度 <span className="tabular-nums text-slate-600">{displayValue(r, py, true)}</span></span>
            : <span className={`font-bold tabular-nums ${colorForRatio(budRatio, r.spec.direction)}`}>予算比 {fmtRatio(budRatio, 1)}</span>}
          <span className={`font-bold tabular-nums ${colorForYoy(cur, py, r.spec.direction)}`}>前年比 {fmtRatioPct(cur, py)}</span>
        </div>
        <div className="mt-0.5 flex items-baseline gap-x-3 flex-wrap text-slate-500">
          {referralRate ? <>
            <span>紹介入院率: <span className="tabular-nums text-slate-600">{displayValue(referralRate, periodCalc(referralRate, "actual"), true)}</span></span>
            <span>前年度 <span className="tabular-nums text-slate-600">{displayValue(referralRate, periodCalc(referralRate, "prevYear"), true)}</span></span>
          </> : <>
            <span>予算 <span className="tabular-nums text-slate-600">{displayValue(r, bud, true)}</span></span>
            <span>前年度 <span className="tabular-nums text-slate-600">{displayValue(r, py, true)}</span></span>
          </>}
        </div>
      </div>
      <div className="mt-2">
        <CompareTrendMini r={r} xDomain={xDomain} embedded />
      </div>
    </div>
  );
}

function CompareTrendMini({ r, xDomain, embedded = false }: { r: ResolvedMetric; xDomain: [Date, Date]; embedded?: boolean }) {
  const skipZero = SKIP_ZERO_KEYS.has(r.spec.key);
  const isMissing = (v: number | null): boolean => v == null || (skipZero && v === 0);
  const isYen = isYenCardMetric(r.spec.key, r.spec.unit);
  const yenOku = useOkuFor(r);
  const gScale = isYen ? 1000 : 1;
  const yenUnit = yenOku ? "億円" : "万円";
  type Row = { date: Date; value: number; series: "今年度" | "予算" | "前年度" };
  type Wide = { date: Date; actual: number | null; budget: number | null; prevYear: number | null };
  const rows: Row[] = [];
  const wide: Wide[] = [];

  r.trend.forEach((p) => {
    const date = new Date(p.date);
    const actual = isMissing(p.actual) ? null : p.actual;
    const budget = isMissing(p.budget) ? null : p.budget;
    const prevYear = isMissing(p.prevYear) ? null : p.prevYear;
    if (actual != null) rows.push({ date, value: actual * gScale, series: "今年度" });
    if (budget != null) rows.push({ date, value: budget * gScale, series: "予算" });
    if (prevYear != null) rows.push({ date, value: prevYear * gScale, series: "前年度" });
    wide.push({ date, actual, budget, prevYear });
  });

  const fmtVal = (v: number | null) => displayValue(r, v, true);
  const options: Plot.PlotOptions = {
    // marginBottom: 月ラベル(buildMonthAxisMark fontSize 11px)が 16px では下半分見切れる → 24px (2026-07-10)
    width: embedded ? 520 : 460, height: embedded ? 116 : 118,
    marginLeft: embedded ? 42 : 48, marginRight: 6, marginTop: 4, marginBottom: 24,
    style: { fontFamily: "var(--jp-font, system-ui)", fontSize: embedded ? "8px" : "9px" },
    x: { label: null, type: "time", axis: null, domain: xDomain },
    y: { label: null, grid: true, nice: true, zero: false,
         tickFormat: (v: number) =>
           isYen ? fmtYenAxisTick(v, yenOku) :
           r.spec.unit === "%" ? `${Math.round(v * 100)}%` :
           r.spec.unit === "日" ? v.toFixed(1) :
           Math.round(v).toLocaleString(),
    },
    marks: [
      Plot.lineY(rows.filter((d) => d.series === "前年度"), { x: "date", y: "value", stroke: "#94a3b8", strokeWidth: 1.5, strokeOpacity: 0.65 }),
      Plot.lineY(rows.filter((d) => d.series === "予算"), { x: "date", y: "value", stroke: "#d97706", strokeWidth: 1.8, strokeDasharray: "5 3" }),
      Plot.lineY(rows.filter((d) => d.series === "今年度"), { x: "date", y: "value", stroke: "#1B9E77", strokeWidth: 2.4 }),
      Plot.dot(rows, { x: "date", y: "value", fill: (d: Row) => d.series === "今年度" ? "#1B9E77" : d.series === "予算" ? "#d97706" : "#94a3b8", r: 1.8 }),
      Plot.tip(wide, Plot.pointerX({
        x: "date",
        y: (d: Wide) => (d.actual ?? d.prevYear ?? d.budget ?? 0) * gScale,
        fontSize: 11,
        title: (d: Wide) => [
          `今年度: ${fmtVal(d.actual)}`,
          `予算  : ${fmtVal(d.budget)}`,
          `前年度: ${fmtVal(d.prevYear)}`,
        ].join("\n"),
      })),
      buildMonthAxisMark(xDomain),
    ],
  };

  if (embedded) {
    return <PlotFigure options={options} responsiveWidth />;
  }

  return (
    <div className="border border-slate-200 rounded px-1.5 py-1 bg-slate-50/30 min-w-0">
      <div className="text-[11px] font-semibold text-slate-700 leading-tight">
        {r.spec.label}
        {(isYen ? yenUnit : r.spec.unit) && <span className="text-slate-500 font-normal"> ({isYen ? yenUnit : r.spec.unit})</span>}
      </div>
      <PlotFigure options={options} responsiveWidth />
    </div>
  );
}

function CompareMatrix({ rows, fy, selectedMonths, colorMode, setColorMode, periodCalc, targetName, rangeLabel, condRows }: {
  rows: ResolvedMatrixRow[]; fy: number; selectedMonths: Set<string>;
  colorMode: ColorMode; setColorMode: (m: ColorMode) => void; periodCalc: PeriodCalc;
  targetName: string; rangeLabel: string; condRows: Record<string, unknown>[];
}) {
  const months = FY_MONTHS.map((m) => fyDate(fy, m));

  // xlsx 出力 (2026-07-13): 行 = 表示中の指標、列 = 月平均 3 値 + 各月の実績/予算/前年度。
  // 値は fact 生値 (千円/円/件/人/日・%系は×100)。表示スケール (億円等) は適用しない。
  // 「出力条件」シートを添付 (どの条件で出力されたデータか追跡できるように)。
  const exportXlsx = async () => {
    const xrows = rows.map((d) => {
      const r = d.r;
      const unit = r.spec.unit ?? "";
      const isPct = unit === "%";
      const conv = (v: number | null) => v == null ? null : isPct ? Math.round(v * 1000) / 10 : v;
      const o: Record<string, unknown> = {
        指標: r.spec.label,
        単位: unit,
        "予算(月平均)": conv(periodCalc(r, "budget")),
        "実績(月平均)": conv(periodCalc(r, "actual")),
        "前年度(月平均)": conv(periodCalc(r, "prevYear")),
      };
      const byDate = new Map(r.trend.map((p) => [p.date, p]));
      // 月列名は YYYYMM (xlsx 側でのソート/参照しやすさ優先・2026-07-13 ユーザ指示。画面表示は monthLabel のまま)
      const ym = (mo: string) => mo.slice(0, 7).replace("-", "");
      for (const mo of months) o[`実績_${ym(mo)}`] = conv(byDate.get(mo)?.actual ?? null);
      for (const mo of months) o[`予算_${ym(mo)}`] = conv(byDate.get(mo)?.budget ?? null);
      for (const mo of months) o[`前年度_${ym(mo)}`] = conv(byDate.get(mo)?.prevYear ?? null);
      return o;
    });
    const { downloadXlsxSheets } = await import("../lib/xlsx");
    await downloadXlsxSheets([
      { name: "月別マトリックス", rows: xrows },
      { name: "出力条件", rows: [
        { 項目: "出力元", 値: "入院実績(前年度・予算比較) 月別マトリックス" },
        { 項目: "対象", 値: targetName.replace(/^[^ ]* /, "") || targetName },
        ...condRows,
      ] },
    ], `月別マトリックス_${fy}年度_${rangeLabel}`);
  };
  const selectedIndexes = months.map((mo, i) => selectedMonths.has(mo) ? i : -1).filter((i) => i >= 0);
  const selectedFirst = selectedIndexes[0] ?? -1;
  const selectedLast = selectedIndexes[selectedIndexes.length - 1] ?? -1;
  const headFrame = (pos: "first" | "middle" | "last" | "only") => [
    "border-t-2 border-slate-900",
    (pos === "first" || pos === "only") ? "border-l-2 border-slate-900" : "",
    (pos === "last" || pos === "only") ? "border-r-2 border-slate-900" : "",
  ].join(" ");
  return (
    <section className="card p-3">
      <div className="flex items-center gap-3 mb-2 flex-wrap">
        <h3 className="text-base font-semibold">月別マトリックス</h3>
        <span className="text-[11px] text-slate-500">上段=今年度、下段=({colorMode === "yoy" ? "前年度" : "予算"})</span>
        {/* 色分けトグルは操作UI = 印刷に出さない (2026-07-10 ユーザ指摘: 印刷に赤いボタンが写る) */}
        <div className="ml-auto inline-flex rounded border border-slate-300 overflow-hidden text-xs no-print">
          <button type="button" onClick={() => setColorMode("yoy")}
            className={`px-3 py-1 ${colorMode === "yoy" ? "bg-amber-500 text-white font-semibold" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
            前年度比で色分け
          </button>
          <button type="button" onClick={() => setColorMode("budget")}
            className={`px-3 py-1 border-l border-slate-300 ${colorMode === "budget" ? "bg-amber-500 text-white font-semibold" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
            予算比で色分け
          </button>
        </div>
        <button type="button" onClick={exportXlsx}
                className="px-3 py-1 rounded border border-slate-300 text-xs text-slate-600 hover:bg-slate-50 no-print">
          ⬇ xlsx
        </button>
      </div>
      <div className="overflow-x-auto text-xs">
        <table className="w-full border-collapse">
          <thead className="bg-slate-100 sticky top-0 z-10">
            <tr>
              <th className="text-left px-2 py-1 border-b border-slate-200 sticky left-0 bg-slate-100 z-20 w-48">指標</th>
              <th className="text-left px-2 py-1 border-b border-slate-200 w-12">単位</th>
              {/* 平均列は月列の前に配置・各列を単独の□で囲む (2026-07-10 ユーザ指示) */}
              <th className={`text-right px-1.5 py-1 border-b border-slate-200 bg-slate-200 ${headFrame("only")}`}>予算(月平均)</th>
              <th className={`text-right px-1.5 py-1 border-b border-slate-200 bg-slate-200 ${headFrame("only")}`}>実績(月平均)</th>
              {months.map((mo, i) => {
                const selected = selectedMonths.has(mo);
                const pos = selectedFirst === selectedLast ? "only" : i === selectedFirst ? "first" : i === selectedLast ? "last" : "middle";
                return (
                  <th key={mo} className={`text-right px-1.5 py-1 border-b border-slate-200 tabular-nums ${selected ? headFrame(pos) : ""}`}>
                    {monthLabel(mo)}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {rows.map((d, i) => (
              <MatrixRow key={d.r.spec.key} r={d.r} thin={!!d.thin} indent={!!d.indent}
                         months={months} selectedMonths={selectedMonths} colorMode={colorMode}
                         index={i} selectedFirst={selectedFirst} selectedLast={selectedLast} isLast={i === rows.length - 1}
                         periodCalc={periodCalc} />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function MatrixRow({ r, thin, indent, months, selectedMonths, colorMode, index, selectedFirst, selectedLast, isLast, periodCalc }: {
  r: ResolvedMetric; thin: boolean; indent: boolean; months: string[]; selectedMonths: Set<string>; colorMode: ColorMode; index: number;
  selectedFirst: number; selectedLast: number; isLast: boolean; periodCalc: PeriodCalc;
}) {
  const byDate = new Map(r.trend.map((p) => [p.date, p]));
  const stripe = index % 2 === 1;
  const rowUnit = isYenCardMetric(r.spec.key, r.spec.unit) ? (useOkuFor(r) ? "億円" : "万円") : r.spec.unit;
  // 期間値: 比指標は Σ分子/Σ分母 (加重)・加算系は月平均 (2026-07-13 平均の平均を排除)
  const curAvg = periodCalc(r, "actual");
  const pyAvg = periodCalc(r, "prevYear");
  const budAvg = periodCalc(r, "budget");
  const fmt = (v: number | null | undefined) => displayValue(r, v, false);
  const bodyFrame = (pos: "first" | "middle" | "last" | "only") => [
    (pos === "first" || pos === "only") ? "border-l-2 border-slate-900" : "",
    (pos === "last" || pos === "only") ? "border-r-2 border-slate-900" : "",
    isLast ? "border-b-2 border-slate-900" : "",
  ].join(" ");

  const labelCls = thin ? "font-normal text-slate-600" : "font-medium";
  const zebra = stripe ? "bg-slate-50" : "bg-white";
  // 実績(月平均) も月セルと同じ色分け (基準はトグルに従い 前年度平均 or 予算平均)。色なしはゼブラ。
  const avgRef = colorMode === "yoy" ? pyAvg : budAvg;
  const avgPct = curAvg != null && avgRef != null && avgRef !== 0 ? (curAvg - avgRef) / Math.abs(avgRef) : null;
  const avgBg = cellBg(avgPct, r.spec.direction);
  return (
    <tr className={`border-b border-slate-100 hover:bg-blue-50/40 ${stripe ? "bg-slate-50" : ""}`}>
      <td className={`text-left px-2 py-0.5 sticky left-0 z-[1] ${labelCls} ${zebra}`}>{indent && "　"}{r.spec.label}</td>
      <td className="text-left px-2 py-0.5 text-slate-500">{rowUnit}</td>
      <td className={`text-right px-1.5 py-0.5 tabular-nums ${zebra} ${bodyFrame("only")}`}>{fmt(budAvg)}</td>
      <td className={`text-right px-1.5 py-0.5 tabular-nums leading-tight ${avgBg || zebra} ${bodyFrame("only")}`}
          title={`期間指定月の平均\n今年度: ${displayValue(r, curAvg, true)}\n前年度: ${displayValue(r, pyAvg, true)}\n予算: ${displayValue(r, budAvg, true)}`}>
        <div>{fmt(curAvg)}</div>
        <div className="text-slate-500">({fmt(avgRef)})</div>
      </td>
      {months.map((mo, i) => {
        const p = byDate.get(mo);
        const actual = p?.actual ?? null;
        const py = p?.prevYear ?? null;
        // 下段表示・色分けの基準とも トグル (前年度比=前年度 / 予算比=予算) に従う
        // (2026-07-10 は下段=常に前年度 → 2026-07-13 数字も基準に連動へ変更)
        const ref = colorMode === "yoy" ? py : (p?.budget ?? null);
        const pct = actual != null && ref != null && ref !== 0 ? (actual - ref) / Math.abs(ref) : null;
        const bg = cellBg(pct, r.spec.direction);
        const selected = selectedMonths.has(mo);
        const pos = selectedFirst === selectedLast ? "only" : i === selectedFirst ? "first" : i === selectedLast ? "last" : "middle";
        return (
          <td key={mo} className={`text-right px-1 py-0.5 tabular-nums leading-tight ${bg} ${selected ? bodyFrame(pos) : ""}`}
              title={`${monthLabel(mo)}\n今年度: ${displayValue(r, actual, true)}\n前年度: ${displayValue(r, py, true)}\n予算: ${displayValue(r, p?.budget ?? null, true)}`}>
            <div>{fmt(actual)}</div>
            <div className="text-slate-500">({fmt(ref)})</div>
          </td>
        );
      })}
    </tr>
  );
}

// ── 科別マトリックス (2026-07-13) ────────────────────────────────────────
// 全診療科 × 主要10指標 (カード8指標 + 手術麻酔収入 + 手術室件数・並び順は月別マトリックス準拠) を
// 期間指定月の月平均で比較する。診療科フィルタはかけない (全科比較が目的のシート)。
// 一番上に病院全体 (総合計) を固定表示し、診療科フォーカスで選んだ科は太枠で囲む。
// 列ヘッダクリックで昇順/降順ソート (既定 = 入院収入 降順)。上段/下段/色分けは月別マトリックスと共通トグル。
// 印刷は 2 枚目をフルに使う (.budget-print-page + .dept-matrix-print)。

const DEPT_MATRIX_FETCH_METRICS = [
  "入院収入", "GP", "手術収入", "手術件数", "入院患者延数", "在院患者延数",
  "入院単価", "在院単価", "GP単価", "GP単価_在院",
  "平均在院日数", "新規受持数", "予定入院率", "緊急入院率", "紹介入院率", "DPC期間2以内率",
  "常勤換算数", "内専攻医常勤換算数",
  // 比指標の加重計算 (Σ分子/Σ分母) 用の分子分母 (2026-07-13。表には出さない)
  // 新規入院数は 紹介入院率 の分母 (表示列は 新規受持数 = 新規入院数+他科から転科)
  "退院患者数", "新規入院数", "予定入院数", "緊急入院数", "他科から転科", "他科への転科", "紹介入院数", "延病床数",
  "入院患者延数(DPC期間2以内)", "入院患者延数(DPC期間2超)",
];

const ALL_MATRIX_SPECS: MetricSpec[] = [...BUDGET_TAB_SPECS, ...COMPARE_EXTRA_SPECS];
const matrixSpecOf = (key: string): MetricSpec | undefined => ALL_MATRIX_SPECS.find((s) => s.key === key);

// 列順 = 月別マトリックス準拠 (収入 → 手術内訳 → 延数 → 単価 → 在院日数 → 入院数系 → 率系)。
// 収入/延数/単価はサイドバーの基準トグルに連動 (カードと同じ解決)。
// 2026-07-13: 先頭に 達成度(ABC・収入列基準)、収入の直後に 予算比/前年比 (色分けトグルに関わらず常時表示)。
function deptMatrixColKeys(revenueBase: "入院収入" | "粗利", volumeBase: "入院" | "在院", yotei: string): string[] {
  const gp = revenueBase === "粗利";
  const zaiin = volumeBase === "在院";
  const revenue = gp ? "GP" : "入院収入";
  const volume = zaiin ? "在院患者延数" : "入院患者延数";
  const unit = gp ? (zaiin ? "GP単価_在院" : "GP単価") : (zaiin ? "在院単価" : "入院単価");
  return ["達成度", revenue, "予算比", "前年比", "手術収入", "手術件数", volume, unit, "平均在院日数", "新規受持数", yotei === "緊急" ? "緊急入院率" : "予定入院率", "紹介入院率", "DPC期間2以内率", "常勤換算数", "内専攻医常勤換算数"];
}

// 科別マトリックスの列名短縮 (2026-07-13 ユーザ指示: 列名整理)
// 手術収入 は spec label 自体を「手術麻酔収入」にしたので override 不要 (2026-07-14)
const DEPT_MATRIX_LABEL_OVERRIDE: Record<string, string> = {
  "常勤換算数": "常勤医師数",
  "内専攻医常勤換算数": "専攻医数",
};
// 画面に出さない科 (手術件数のみの非入院科)。xlsx 出力には含める (2026-07-13 ユーザ指示)
const DEPT_MATRIX_HIDDEN = new Set(["麻酔科"]);

type DeptMatrixAvg = { actual: number | null; budget: number | null; prevYear: number | null };
type DeptMatrixRowData = {
  dept: string;
  cells: (DeptMatrixAvg | null)[];
  // 達成度/予算比/前年比の計算源 = 収入列 (revenue) の月平均
  grade: "A" | "B" | "C" | null;
  revActual: number | null;
  revBudget: number | null;
  revPrevYear: number | null;
};
type DeptMatrixColMeta = {
  key: string; label: string; unitLabel: string; direction: Direction;
  special?: "grade" | "budRatio" | "yoyRatio";
  fmt: (v: number | null) => string;
};

function DeptMatrix({ fy, selectedMonths, colorMode, focusedDept, revenueBase, volumeBase, rangeLabel, condRows, focusRowOnly = false }: {
  fy: number; selectedMonths: Set<string>; colorMode: ColorMode; focusedDept: string | null;
  revenueBase: "入院収入" | "粗利"; volumeBase: "入院" | "在院"; rangeLabel: string;
  condRows: Record<string, unknown>[];
  /** [2026-08-03 医師別タブ] 診療科フォーカス中はその 1 行だけにする (画面・印刷とも)。
   *  「科の 1 行 + その科の医師」で 1 枚に収める構成なので、画面と紙の見た目を一致させる。
   *  ★正式版タブ (この prop を渡さない) は従来どおり全科表示のまま = 全科比較シートとして残す。 */
  focusRowOnly?: boolean;
}) {
  const f = useFilters();
  const { data, isLoading } = useQuery<DeptMatrixResponse>({
    queryKey: ["dept_matrix", f.granularity, f.iryo, f.yotei, fy, f.routeKeiyu],
    queryFn: () => api.deptMatrix({
      granularity: f.granularity, iryo: f.iryo, yotei: f.yotei,
      fyStart: `${fy}-04-01`, fyEnd: `${fy + 1}-03-01`, metrics: DEPT_MATRIX_FETCH_METRICS,
      keiyu: f.routeKeiyu,
    }),
    placeholderData: (prev) => prev,
  });
  const [sort, setSort] = useState<{ key: string; dir: "asc" | "desc" }>({ key: "前年比", dir: "desc" });
  const cols = useMemo(() => deptMatrixColKeys(revenueBase, volumeBase, f.yotei), [revenueBase, volumeBase, f.yotei]);

  const { totalRow, allDeptRows, colMeta } = useMemo(() => {
    if (!data) return { totalRow: null as DeptMatrixRowData | null, allDeptRows: [] as DeptMatrixRowData[], colMeta: [] as DeptMatrixColMeta[] };
    // (診療科|集計区分) → 期間指定月の平均 (月別マトリックスの 実績/予算(月平均) と同じ計算)
    const avg = new Map<string, DeptMatrixAvg>();
    const deptSet = new Set<string>();
    for (const r of data.rows) {
      const skipZero = SKIP_ZERO_KEYS.has(r.集計区分);
      const mean = (field: "actual" | "budget" | "prevYear"): number | null => {
        const vals = r.trend
          .filter((p) => selectedMonths.has(p.date))
          .map((p) => p[field])
          .filter((v): v is number => v != null && Number.isFinite(v) && !(skipZero && v === 0));
        return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
      };
      avg.set(`${r.診療科}|${r.集計区分}`, { actual: mean("actual"), budget: mean("budget"), prevYear: mean("prevYear") });
      deptSet.add(r.診療科);
    }
    const totalLike = new Set(["総合計", "急性期合計", "回復期合計"]);
    const depts = Array.from(deptSet).filter((d) => !totalLike.has(d));
    // 比指標は 科×期間 で Σ分子/Σ分母 (加重)。分子分母が無い field は単純平均へフォールバック (2026-07-13)
    const trendMap = new Map(data.rows.map((r) => [`${r.診療科}|${r.集計区分}`, r.trend]));
    const cellOf = (dept: string, key: string): DeptMatrixAvg | null => {
      const base = avg.get(`${dept}|${key}`) ?? null;
      const spec = (f.yotei !== "合計" && (key === "予定入院率" || key === "緊急入院率"))
        ? { num: ["入院構成_分子"], den: ["入院構成_分母"] }
        : RATIO_METRICS[key];
      if (!spec || spec.unitDays) return base;
      const val = (field: "actual" | "budget" | "prevYear"): number | null => {
        const sumOf = (m: string): number | null => {
          const tr = trendMap.get(`${dept}|${m}`);
          if (!tr) return null;
          const vals = tr.filter((p) => selectedMonths.has(p.date)).map((p) => p[field])
            .filter((v): v is number => v != null && Number.isFinite(v));
          return vals.length ? vals.reduce((a, b) => a + b, 0) : null;
        };
        return weightedRatio(spec, sumOf) ?? base?.[field] ?? null;
      };
      return { actual: val("actual"), budget: val("budget"), prevYear: val("prevYear") };
    };
    // 列メタ: 円系は列内 |max| で 億/万円 スケールを列単位に固定 (行間で桁を揃える)
    const noFmt = (v: number | null) => v == null ? "—" : String(v);
    const colMeta: DeptMatrixColMeta[] = cols.map((key) => {
      if (key === "達成度") return { key, label: "達成度", unitLabel: "", direction: "neutral" as Direction, special: "grade" as const, fmt: noFmt };
      if (key === "予算比") return { key, label: "予算比", unitLabel: "%", direction: "neutral" as Direction, special: "budRatio" as const, fmt: noFmt };
      if (key === "前年比") return { key, label: "前年比", unitLabel: "%", direction: "neutral" as Direction, special: "yoyRatio" as const, fmt: noFmt };
      const spec = matrixSpecOf(key);
      const unit = spec?.unit ?? "";
      const direction: Direction = spec?.direction ?? "neutral";
      const label = admissionVolumeLabel(key, f.yotei) ?? DEPT_MATRIX_LABEL_OVERRIDE[key] ?? spec?.label ?? key;
      if (isYenCardMetric(key, unit)) {
        let maxAbs = 0;
        for (const d of [data.totalDept, ...depts]) {
          const a = avg.get(`${d}|${key}`);
          for (const v of [a?.actual, a?.budget, a?.prevYear]) if (v != null) maxAbs = Math.max(maxAbs, Math.abs(v));
        }
        const oku = yenUseOku(maxAbs);
        return { key, label, unitLabel: oku ? "億円" : "万円", direction,
                 fmt: (v: number | null) => v == null ? "—" : fmtYenScaled(v, oku).value };
      }
      // 手術件数の月平均は小数1位 (2026-07-13 ユーザ指示: 3ヶ月平均等で整数丸めだと不自然)
      if (key === "手術件数") {
        return { key, label, unitLabel: unit, direction,
                 fmt: (v: number | null) => v == null ? "—" : v.toLocaleString("ja-JP", { minimumFractionDigits: 1, maximumFractionDigits: 1 }) };
      }
      return { key, label, unitLabel: unit ?? "", direction,
               fmt: (v: number | null) => v == null ? "—" : fmtByUnitValueOnly(v, unit) };
    });
    // 達成度/予算比/前年比の計算源 = 収入列 ("達成度" の直後 = cols[1])
    const revenueKey = cols[1];
    const rowOf = (dept: string): DeptMatrixRowData => {
      const rev = cellOf(dept, revenueKey);
      const revActual = rev?.actual ?? null;
      const revBudget = rev?.budget ?? null;
      const revPrevYear = rev?.prevYear ?? null;
      return {
        dept,
        cells: cols.map((k) => cellOf(dept, k)),
        grade: gradeOf(revActual, revBudget, revPrevYear, "higher_is_better"),
        revActual, revBudget, revPrevYear,
      };
    };
    // ソート (指標列 = 実績の月平均 / 達成度 = A>B>C / 予算比・前年比 = 収入列の比。null は末尾・科名順で安定化)
    const sortKey = cols.includes(sort.key) ? sort.key : "前年比";
    const gradeRank = { A: 3, B: 2, C: 1 } as const;
    const sVal = (d: string): number | null => {
      const rev = cellOf(d, revenueKey);
      if (sortKey === "達成度") {
        const g = gradeOf(rev?.actual ?? null, rev?.budget ?? null, rev?.prevYear ?? null, "higher_is_better");
        return g ? gradeRank[g] : null;
      }
      if (sortKey === "予算比") {
        return rev?.actual != null && rev?.budget != null && rev.budget !== 0 ? rev.actual / rev.budget : null;
      }
      if (sortKey === "前年比") {
        return rev?.actual != null && rev?.prevYear != null && rev.prevYear !== 0 ? rev.actual / rev.prevYear : null;
      }
      return cellOf(d, sortKey)?.actual ?? null;
    };
    depts.sort((a, b) => {
      const va = sVal(a), vb = sVal(b);
      if (va == null && vb == null) return a.localeCompare(b, "ja");
      if (va == null) return 1;
      if (vb == null) return -1;
      if (va === vb) return a.localeCompare(b, "ja");
      return sort.dir === "desc" ? vb - va : va - vb;
    });
    return { totalRow: rowOf(data.totalDept), allDeptRows: depts.map(rowOf), colMeta };
  }, [data, selectedMonths, cols, sort, f.yotei]);

  // 画面表示は 麻酔科 を出さない (手術件数のみの非入院科・2026-07-13 ユーザ指示)。
  // 総合計のΣには寄与済み・xlsx 出力には含める。
  const deptRows = allDeptRows.filter((r) => !DEPT_MATRIX_HIDDEN.has(r.dept));

  const onSort = (key: string) =>
    setSort((s) => s.key === key ? { key, dir: s.dir === "desc" ? "asc" : "desc" } : { key, dir: "desc" });
  const effectiveSortKey = cols.includes(sort.key) ? sort.key : "前年比";

  // 行クリックで診療科フォーカス (同じ科を再クリック or 病院全体クリックで解除)
  const onRowClick = (dept: string | null) =>
    f.setFocusedDept(dept == null || f.focusedDept === dept ? null : dept);

  // [2026-08-03 ユーザ指示] 診療科を選んだらこの表もその 1 行だけにする (画面・印刷とも)。
  //   ★以前は印刷だけ 1 行にして画面は全科のままだったが、「画面で選んだのに絞り込まれない」
  //     「紙と見た目が違う」ため、**データ段階で絞る**方式に変更した (印刷用の CSS 退避は不要になった)。
  //   解除は下の「× 全科に戻す」ボタン (no-print) から。
  const focusOne = focusRowOnly && !!focusedDept;
  const shownDeptRows = focusOne ? deptRows.filter((r) => r.dept === focusedDept) : deptRows;

  // xlsx 出力: 表示ソート順・麻酔科含む全科。値は fact 生値 (千円/円/件/人/日・%系は×100)。
  const exportXlsx = async () => {
    if (!totalRow) return;
    const pct = (v: number | null) => v == null ? null : Math.round(v * 1000) / 10;
    const rows = [totalRow, ...allDeptRows].map((r) => {
      const o: Record<string, unknown> = { 診療科: r.dept === data?.totalDept ? "病院全体" : r.dept };
      o["達成度"] = r.grade ?? "";
      o["予算比(%)"] = pct(r.revActual != null && r.revBudget != null && r.revBudget !== 0 ? r.revActual / r.revBudget : null);
      o["前年比(%)"] = pct(r.revActual != null && r.revPrevYear != null && r.revPrevYear !== 0 ? r.revActual / r.revPrevYear : null);
      colMeta.forEach((cm, j) => {
        if (cm.special) return;
        const rawUnit = matrixSpecOf(cm.key)?.unit ?? "";
        const isPct = rawUnit === "%";
        const suffix = isPct ? "(%)" : rawUnit ? `(${rawUnit})` : "";
        const conv = (v: number | null) => v == null ? null : isPct ? pct(v) : v;
        const c = r.cells[j];
        o[`${cm.label}_実績${suffix}`] = conv(c?.actual ?? null);
        o[`${cm.label}_予算${suffix}`] = conv(c?.budget ?? null);
        o[`${cm.label}_前年度${suffix}`] = conv(c?.prevYear ?? null);
      });
      return o;
    });
    const { downloadXlsxSheets } = await import("../lib/xlsx");
    await downloadXlsxSheets([
      { name: "科別マトリックス", rows },
      { name: "出力条件", rows: [
        { 項目: "出力元", 値: "入院実績(前年度・予算比較) 科別マトリックス" },
        { 項目: "対象", 値: "全診療科 (麻酔科は画面非表示だが本xlsxに含む)" },
        ...condRows,
      ] },
    ], `科別マトリックス_${fy}年度_${rangeLabel}`);
  };

  return (
    <section className="card p-3">
      <div className="flex items-center gap-3 mb-2 flex-wrap">
        <h3 className="text-base font-semibold">科別マトリックス</h3>
        <span className="text-[11px] text-slate-500">
          {focusOne ? <>🎯 <b>{focusedDept}</b></> : "全診療科"} × 主要指標 (期間 {rangeLabel} の月平均)。上段=今年度、下段=({colorMode === "yoy" ? "前年度" : "予算"})。
          {f.yotei !== "合計" && f.iryo === "合計" && f.routeKeiyu === "合計" && " 予定/緊急別予算は前年同月構成比による推計。"}
        </span>
        {focusOne && (
          <button type="button" onClick={() => f.setFocusedDept(null)}
                  className="no-print px-2 py-0.5 rounded border border-slate-300 text-[11px] text-slate-600 hover:bg-slate-50"
                  title="診療科の絞り込みを解除して全科表示に戻す">
            × 全科に戻す
          </button>
        )}
        {isLoading && <span className="text-[11px] text-slate-400">loading…</span>}
        <button type="button" onClick={exportXlsx}
                className="ml-auto px-3 py-1 rounded border border-slate-300 text-xs text-slate-600 hover:bg-slate-50 no-print">
          ⬇ xlsx
        </button>
      </div>
      <div className="overflow-x-auto text-xs">
        <table className="w-full border-collapse">
          <thead className="bg-slate-100 sticky top-0 z-10">
            <tr>
              <th className="text-left px-2 py-1 border-b border-slate-200 sticky left-0 bg-slate-100 z-20 w-40">診療科</th>
              {colMeta.map((cm) => (
                <th key={cm.key}
                    className="text-right px-1.5 py-1 border-b border-slate-200 cursor-pointer select-none hover:bg-slate-200"
                    onClick={() => onSort(cm.key)}
                    title="クリックで並べ替え (昇順/降順)">
                  <div className="leading-tight">
                    {cm.label}
                    {effectiveSortKey === cm.key && <span className="ml-0.5">{sort.dir === "desc" ? "▼" : "▲"}</span>}
                  </div>
                  <div className="font-normal text-[10px] text-slate-500">{cm.unitLabel}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {totalRow && !focusOne && (
              <DeptMatrixTr row={totalRow} label="🏥 病院全体" colMeta={colMeta} colorMode={colorMode}
                            emphasized={false} className="bg-slate-100 font-semibold border-b-2 border-slate-400"
                            onClick={() => onRowClick(null)} />
            )}
            {shownDeptRows.map((row, i) => (
              <DeptMatrixTr key={row.dept} row={row} label={row.dept} colMeta={colMeta} colorMode={colorMode}
                            emphasized={focusedDept === row.dept}
                            className={i % 2 === 1 ? "bg-slate-50" : "bg-white"}
                            onClick={() => onRowClick(row.dept)} />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function DeptMatrixTr({ row, label, colMeta, colorMode, emphasized, className, onClick }: {
  row: DeptMatrixRowData; label: string; colMeta: DeptMatrixColMeta[]; colorMode: ColorMode;
  emphasized: boolean; className: string; onClick?: () => void;
}) {
  // フォーカス科の太枠: tr に outline が効かないブラウザがあるため td の border で囲む
  const emphY = emphasized ? "border-t-2 border-b-2 border-slate-900" : "";
  return (
    <tr className={`border-b border-slate-100 hover:bg-blue-50/40 ${className} ${onClick ? "cursor-pointer" : ""}`}
        onClick={onClick} title={onClick ? "クリックで診療科フォーカス (再クリックで解除)" : undefined}>
      <td className={`text-left px-2 py-0.5 sticky left-0 z-[1] ${className} ${emphY} ${emphasized ? "border-l-2 font-semibold" : ""}`}>
        {emphasized && "🎯 "}{label}
      </td>
      {row.cells.map((cell, j) => {
        const cm = colMeta[j];
        const isLast = j === row.cells.length - 1;
        const tdBase = `px-1.5 py-0.5 tabular-nums leading-tight ${emphY} ${emphasized && isLast ? "border-r-2 border-slate-900" : ""}`;
        // 特殊列 (達成度/予算比/前年比): 色分けトグルに関わらず常時表示・セル背景色分けなし (文字色/バッジで示す)
        if (cm.special === "grade") {
          return (
            <td key={cm.key} className={`text-center ${tdBase}`}>
              {row.grade
                ? <span className={`grade-circle grade-circle-sm rounded-full text-[11px] font-extrabold ${gradeStyle(row.grade)}`}>{row.grade}</span>
                : <span className="text-slate-400">—</span>}
            </td>
          );
        }
        if (cm.special === "budRatio") {
          const ratio = row.revActual != null && row.revBudget != null && row.revBudget !== 0 ? row.revActual / row.revBudget : null;
          return (
            <td key={cm.key} className={`text-right ${tdBase}`}>
              <span className={`font-bold ${colorForRatio(ratio, "higher_is_better")}`}>{fmtRatio(ratio, 1)}</span>
            </td>
          );
        }
        if (cm.special === "yoyRatio") {
          return (
            <td key={cm.key} className={`text-right ${tdBase}`}>
              <span className={`font-bold ${colorForYoy(row.revActual, row.revPrevYear, "higher_is_better")}`}>{fmtRatioPct(row.revActual, row.revPrevYear)}</span>
            </td>
          );
        }
        const actual = cell?.actual ?? null;
        const ref = colorMode === "yoy" ? (cell?.prevYear ?? null) : (cell?.budget ?? null);
        const pct = actual != null && ref != null && ref !== 0 ? (actual - ref) / Math.abs(ref) : null;
        const bg = cellBg(pct, cm.direction);
        return (
          <td key={cm.key} className={`text-right ${bg} ${tdBase}`}>
            <div>{cm.fmt(actual)}</div>
            <div className="text-slate-500">({cm.fmt(ref)})</div>
          </td>
        );
      })}
    </tr>
  );
}
